/**
 * Nosto-FacetRenderer.js
 *
 * Renderiza los facets devueltos por Nosto utilizando la estructura
 * visual de filtros del theme.
 *
 * Conecta checkboxes, grupos de color y rangos de precio con una
 * instancia de NostoFacetManager.
 *
 * Este archivo puede utilizarse tanto en PLP como en Search.
 * No ejecuta búsquedas, no administra paginación y no contiene
 * lógica específica de ninguno de los controllers.
 */

(function initializeNostoFacetRenderer(global) {
    "use strict";

    class NostoFacetRenderer {
        /**
         * Crea una instancia independiente para renderizar facets.
         */
        constructor({
            container,
            facetManager,

            labels = {},
            valueLabels = {},

            fieldOrder = [],
            hiddenFields = [],

            colorField = "",
            priceField = "price",
            sizeField = "",
            sizeOrder = [],

            maxVisibleItems = 10,
            defaultOpen = false,

            currency = "MXN",
            currencySymbol = "$",
            locale = "es-MX",

            autoApply = false,

            applyButton = null,
            clearButton = null,

            showCounts = true,
            showPriceLabels = false,

            onApply = null,
            onClear = null,
            onRender = null,

            debug = false,
        } = {}) {
            this.container =
                this.resolveElement(container);

            this.facetManager =
                facetManager;

            this.labels = {
                ...labels,
            };

            this.valueLabels = {
                ...valueLabels,
            };

            this.fieldOrder =
                Array.isArray(fieldOrder)
                    ? [...fieldOrder]
                    : [];

            this.hiddenFields =
                new Set(
                    Array.isArray(hiddenFields)
                        ? hiddenFields
                        : []
                );

            this.colorField =
                this.normalizeString(
                    colorField
                );

            this.priceField =
                this.normalizeString(
                    priceField
                ) || "price";

            this.sizeField =
                this.normalizeString(
                    sizeField
                );

            this.sizeOrder =
                Array.isArray(sizeOrder)
                    ? sizeOrder.map((value) =>
                        this.normalizeFacetValue(
                            value
                        )
                    )
                    : [];

            this.maxVisibleItems =
                this.normalizePositiveInteger(
                    maxVisibleItems,
                    10
                );

            this.defaultOpen =
                Boolean(defaultOpen);

            this.currency =
                this.normalizeString(
                    currency
                ) || "MXN";

            this.currencySymbol =
                this.normalizeString(
                    currencySymbol
                ) || "$";

            this.locale =
                this.normalizeString(
                    locale
                ) || "es-MX";

            this.autoApply =
                Boolean(autoApply);

            this.applyButton =
                this.resolveElement(
                    applyButton
                );

            this.clearButton =
                this.resolveElement(
                    clearButton
                );

            this.showCounts =
                Boolean(showCounts);

            this.showPriceLabels =
                Boolean(showPriceLabels);

            this.onApply =
                typeof onApply === "function"
                    ? onApply
                    : null;

            this.onClear =
                typeof onClear === "function"
                    ? onClear
                    : null;

            this.onRender =
                typeof onRender === "function"
                    ? onRender
                    : null;

            this.debug =
                Boolean(debug);

            this.facets = [];
            this.isBound = false;

            this.appliedSnapshot =
                null;

            this.isDirty = false;

            this.boundHandleChange =
                this.handleChange.bind(this);

            this.boundHandleInput =
                this.handleInput.bind(this);

            this.boundHandleClick =
                this.handleClick.bind(this);

            this.boundHandleRangeUpdate =
                this.handleRangeUpdate.bind(
                    this
                );

            this.boundHandleApply =
                this.handleApply.bind(this);

            this.boundHandleClear =
                this.handleClear.bind(this);

            this.markCurrentStateAsApplied({
                updateButtons: false,
            });
        }

        /**
         * Escribe mensajes en consola cuando el modo debug está activo.
         */
        log(...args) {
            if (!this.debug) return;

            console.info(
                "[NostoFacetRenderer]",
                ...args
            );
        }

        /**
         * Convierte un valor en una cadena limpia.
         */
        normalizeString(value) {
            return String(value ?? "").trim();
        }

        /**
         * Normaliza un valor para realizar comparaciones entre facets.
         */
        normalizeFacetValue(value) {
            return this
                .normalizeString(value)
                .toUpperCase();
        }

        /**
         * Convierte un valor en un entero positivo.
         */
        normalizePositiveInteger(
            value,
            fallback
        ) {
            const number = Number(value);

            return (
                Number.isFinite(number) &&
                number > 0
            )
                ? Math.floor(number)
                : fallback;
        }

        /**
         * Convierte un valor en un número o devuelve null.
         */
        normalizeNullableNumber(value) {
            if (
                value == null ||
                value === ""
            ) {
                return null;
            }

            const number = Number(value);

            return Number.isFinite(number)
                ? number
                : null;
        }

        /**
 * Normaliza un valor para poder comparar estados de filtros.
 */
        normalizeComparableValue(value) {
            if (Array.isArray(value)) {
                return value
                    .map((item) =>
                        this.normalizeComparableValue(
                            item
                        )
                    )
                    .sort((itemA, itemB) =>
                        JSON.stringify(itemA)
                            .localeCompare(
                                JSON.stringify(itemB)
                            )
                    );
            }

            if (
                value &&
                typeof value === "object"
            ) {
                return Object.keys(value)
                    .sort()
                    .reduce(
                        (
                            normalizedObject,
                            key
                        ) => {
                            normalizedObject[key] =
                                this.normalizeComparableValue(
                                    value[key]
                                );

                            return normalizedObject;
                        },
                        {}
                    );
            }

            return value ?? null;
        }

        /**
         * Crea una representación comparable del estado actual.
         */
        createComparableSnapshot() {
            if (!this.facetManager) {
                return "";
            }

            const snapshot = {
                state:
                    this.facetManager
                        .getState(),

                selectedColorGroupsByField:
                    this.facetManager
                        .getColorGroupState?.() ||
                    {},
            };

            return JSON.stringify(
                this.normalizeComparableValue(
                    snapshot
                )
            );
        }

        /**
         * Comprueba si el estado actual difiere del último estado aplicado.
         */
        calculateDirtyState() {
            if (this.autoApply) {
                return false;
            }

            if (
                this.appliedSnapshot == null
            ) {
                return false;
            }

            return (
                this.createComparableSnapshot() !==
                this.appliedSnapshot
            );
        }

        /**
         * Recalcula y almacena el estado dirty del renderer.
         */
        updateDirtyState({
            updateButtons = true,
        } = {}) {
            this.isDirty =
                this.calculateDirtyState();

            this.container?.classList.toggle(
                "is-dirty",
                this.isDirty
            );

            this.container?.setAttribute(
                "data-nosto-facets-dirty",
                String(this.isDirty)
            );

            if (updateButtons) {
                this.updateButtons();
            }

            return this.isDirty;
        }

        /**
         * Devuelve la cantidad total de selecciones actualmente activas.
         *
         * Los grupos de color cuentan como una selección por grupo y
         * el rango de precio cuenta como una sola selección.
         */
        getAppliedActiveFilterCount() {
            return Number(
                this.facetManager
                    ?.getTotalActiveCount?.() ||
                0
            );
        }

        /**
         * Sincroniza todos los badges compartidos del botón y encabezado.
         */
        updateActiveFilterCountBadges(
            count =
                this.getAppliedActiveFilterCount()
        ) {
            const normalizedCount =
                Math.max(
                    0,
                    Number(count) || 0
                );

            document
                .querySelectorAll(
                    "[data-nosto-active-filter-count]"
                )
                .forEach((badge) => {
                    badge.textContent =
                        String(normalizedCount);

                    badge.dataset.activeCount =
                        String(normalizedCount);

                    badge.hidden =
                        normalizedCount === 0;

                    badge.classList.toggle(
                        "hidden",
                        normalizedCount === 0
                    );

                    badge.setAttribute(
                        "aria-label",
                        `${normalizedCount} ${normalizedCount === 1
                            ? "filtro activo"
                            : "filtros activos"
                        }`
                    );
                });

            return normalizedCount;
        }

        /**
         * Marca el estado actual como el último estado aplicado.
         */
        markCurrentStateAsApplied({
            updateButtons = true,
        } = {}) {
            this.appliedSnapshot =
                this.createComparableSnapshot();

            this.isDirty = false;

            this.container?.classList.remove(
                "is-dirty"
            );

            this.container?.setAttribute(
                "data-nosto-facets-dirty",
                "false"
            );

            this.updateActiveFilterCountBadges();

            if (updateButtons) {
                this.updateButtons();
            }

            return this.appliedSnapshot;
        }

        /**
         * Devuelve si existen cambios pendientes por aplicar.
         */
        hasPendingChanges() {
            return this.updateDirtyState({
                updateButtons: false,
            });
        }

        /**
         * Resuelve un elemento desde un selector CSS o devuelve el nodo recibido.
         */
        resolveElement(target) {
            if (target instanceof Element) {
                return target;
            }

            if (typeof target === "string") {
                return document.querySelector(
                    target
                );
            }

            return null;
        }

        /**
         * Valida que existan el contenedor y el FacetManager.
         */
        validateDependencies() {
            if (!this.container) {
                throw new Error(
                    "NostoFacetRenderer requiere un contenedor válido."
                );
            }

            if (!this.facetManager) {
                throw new Error(
                    "NostoFacetRenderer requiere una instancia de NostoFacetManager."
                );
            }
        }

        /**
         * Escapa texto para insertarlo de forma segura dentro de HTML.
         */
        escapeHtml(value) {
            return this
                .normalizeString(value)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;");
        }

        /**
         * Escapa un valor para utilizarlo dentro de un atributo HTML.
         */
        escapeAttribute(value) {
            return this.escapeHtml(value);
        }

        /**
         * Convierte un valor en un identificador HTML seguro.
         */
        createId(value) {
            return this
                .normalizeString(value)
                .toLowerCase()
                .normalize("NFD")
                .replace(
                    /[\u0300-\u036f]/g,
                    ""
                )
                .replace(
                    /[^a-z0-9_-]+/g,
                    "-"
                )
                .replace(
                    /^-+|-+$/g,
                    ""
                );
        }

        /**
         * Devuelve la etiqueta configurada para un facet.
         */
        getFacetLabel(facet) {
            const field =
                this.normalizeString(
                    facet?.field
                );

            const name =
                this.normalizeString(
                    facet?.name
                );

            return (
                this.labels[field] ||
                this.labels[name] ||
                name ||
                field
            );
        }

        /**
         * Devuelve la etiqueta configurada para un valor específico.
         */
        getValueLabel(
            field,
            value
        ) {
            const normalizedValue =
                this.normalizeFacetValue(
                    value
                );

            return (
                this.valueLabels?.[
                field
                ]?.[normalizedValue] ||
                this.valueLabels?.[
                normalizedValue
                ] ||
                value
            );
        }

        /**
         * Comprueba si un facet debe omitirse del renderizado.
         */
        shouldHideFacet(facet) {
            const field =
                this.normalizeString(
                    facet?.field
                );

            if (!field) {
                return true;
            }

            if (
                this.hiddenFields.has(field)
            ) {
                return true;
            }

            const definition =
                this.facetManager
                    .getDefinition?.(field);

            return Boolean(
                definition?.ignored
            );
        }

        /**
         * Comprueba si un facet representa un rango de precio.
         */
        isPriceFacet(facet) {
            return (
                this.normalizeString(
                    facet?.field
                ) === this.priceField ||
                (
                    facet?.type === "stats" &&
                    this.normalizeString(
                        facet?.field
                    ) === this.priceField
                )
            );
        }

        /**
         * Comprueba si un facet representa grupos de color.
         */
        isColorFacet(facet) {
            return (
                Boolean(this.colorField) &&
                this.normalizeString(
                    facet?.field
                ) === this.colorField
            );
        }

        /**
         * Ordena los facets de acuerdo con la configuración fieldOrder.
         */
        sortFacets(facets = []) {
            if (!this.fieldOrder.length) {
                return [...facets];
            }

            const orderMap =
                new Map(
                    this.fieldOrder.map(
                        (field, index) => [
                            field,
                            index,
                        ]
                    )
                );

            return [...facets].sort(
                (facetA, facetB) => {
                    const indexA =
                        orderMap.has(facetA.field)
                            ? orderMap.get(
                                facetA.field
                            )
                            : Number.MAX_SAFE_INTEGER;

                    const indexB =
                        orderMap.has(facetB.field)
                            ? orderMap.get(
                                facetB.field
                            )
                            : Number.MAX_SAFE_INTEGER;

                    return indexA - indexB;
                }
            );
        }

        /**
         * Obtiene la lista de valores disponible dentro de un facet.
         */
        getFacetValues(facet) {
            const values =
                facet?.data ??
                facet?.values ??
                [];

            return Array.isArray(values)
                ? values
                : [];
        }

        /**
         * Devuelve el valor textual de una opción de facet.
         */
        getFacetItemValue(facetItem) {
            return this.normalizeString(
                facetItem?.value ??
                facetItem?.name ??
                facetItem?.key
            );
        }

        /**
         * Obtiene el conteo de productos de una opción de facet.
         */
        getFacetValueCount(facetValue) {
            const count = Number(
                facetValue?.count ??
                facetValue?.frequency ??
                facetValue?.docCount ??
                facetValue?.total ??
                0
            );

            return Number.isFinite(count)
                ? count
                : 0;
        }

        /**
         * Ordena los valores de un facet, incluyendo el orden personalizado de tallas.
         */
        sortFacetValues(
            facet,
            values = []
        ) {
            const field =
                this.normalizeString(
                    facet?.field
                );

            const sortedValues =
                [...values];

            if (
                field === this.sizeField &&
                this.sizeOrder.length
            ) {
                return sortedValues.sort(
                    (valueA, valueB) => {
                        const normalizedA =
                            this.normalizeFacetValue(
                                this.getFacetItemValue(
                                    valueA
                                )
                            );

                        const normalizedB =
                            this.normalizeFacetValue(
                                this.getFacetItemValue(
                                    valueB
                                )
                            );

                        const indexA =
                            this.sizeOrder.indexOf(
                                normalizedA
                            );

                        const indexB =
                            this.sizeOrder.indexOf(
                                normalizedB
                            );

                        if (
                            indexA === -1 &&
                            indexB === -1
                        ) {
                            return normalizedA.localeCompare(
                                normalizedB,
                                this.locale
                            );
                        }

                        if (indexA === -1) {
                            return 1;
                        }

                        if (indexB === -1) {
                            return -1;
                        }

                        return indexA - indexB;
                    }
                );
            }

            return sortedValues.sort(
                (valueA, valueB) =>
                    this
                        .getFacetItemValue(valueA)
                        .localeCompare(
                            this.getFacetItemValue(
                                valueB
                            ),
                            this.locale
                        )
            );
        }

        /**
         * Crea un mapa de los valores disponibles y sus conteos.
         */
        createAvailableValueMap(facet) {
            const availableValues =
                new Map();

            for (
                const item of this.getFacetValues(
                    facet
                )
            ) {
                const value =
                    this.normalizeFacetValue(
                        this.getFacetItemValue(
                            item
                        )
                    );

                if (!value) continue;

                availableValues.set(
                    value,
                    {
                        item,
                        count:
                            this.getFacetValueCount(
                                item
                            ),
                    }
                );
            }

            return availableValues;
        }

        /**
 * Obtiene el icono original de Broadcast para Mostrar más.
 */
        renderShowMoreIcon() {
            const template =
                document.querySelector(
                    "[data-nosto-show-more-icon]"
                );

            return template
                ?.content
                ?.cloneNode(true)
                ?.firstElementChild
                ?.outerHTML ||
                "";
        }

        /**
         * Agrupa los tonos reales de Nosto según los grupos de color configurados.
         */
        getColorGroups(facet) {
            const availableValues =
                this.createAvailableValueMap(
                    facet
                );

            const groups = [];

            for (
                const [
                    groupName,
                    configuredValues,
                ] of Object.entries(
                    this.facetManager
                        .colorGroups || {}
                )
            ) {
                const matchingValues = [];
                let totalCount = 0;

                for (
                    const configuredValue of configuredValues
                ) {
                    const normalizedValue =
                        this.normalizeFacetValue(
                            configuredValue
                        );

                    const available =
                        availableValues.get(
                            normalizedValue
                        );

                    if (!available) continue;

                    matchingValues.push(
                        normalizedValue
                    );

                    totalCount +=
                        available.count;
                }

                if (!matchingValues.length) {
                    continue;
                }

                groups.push({
                    value: groupName,

                    label:
                        this.getValueLabel(
                            facet.field,
                            groupName
                        ),

                    rawValues:
                        matchingValues,

                    count:
                        totalCount,
                });
            }

            return groups.sort(
                (groupA, groupB) =>
                    groupA.label.localeCompare(
                        groupB.label,
                        this.locale
                    )
            );
        }

        /**
         * Formatea un valor monetario para mostrarlo en la interfaz.
         */
        formatMoney(value) {
            const number =
                Number(value || 0);

            return new Intl.NumberFormat(
                this.locale,
                {
                    style: "currency",
                    currency: this.currency,
                    maximumFractionDigits: 0,
                }
            ).format(number);
        }

        /**
         * Extrae los límites absolutos desde un facet de precio de Nosto.
         */
        getPriceBounds(facet) {
            const min =
                this.normalizeNullableNumber(
                    facet?.min?.parsedValue ??
                    facet?.min?.value ??
                    facet?.min?.source ??
                    facet?.min ??
                    0
                ) ?? 0;

            const max =
                this.normalizeNullableNumber(
                    facet?.max?.parsedValue ??
                    facet?.max?.value ??
                    facet?.max?.source ??
                    facet?.max ??
                    0
                ) ?? 0;

            return min <= max
                ? {
                    min,
                    max,
                }
                : {
                    min: max,
                    max: min,
                };
        }

        /**
         * Devuelve el estado visual actual del filtro de precio.
         */
        getPriceState(facet) {
            const bounds =
                this.getPriceBounds(facet);

            this.facetManager.setPriceBounds(
                bounds.min,
                bounds.max,
                {
                    normalizeCurrentRange:
                        true,
                }
            );

            const selectedRange =
                this.facetManager.getRange(
                    this.priceField
                ) || {
                    min: null,
                    max: null,
                };

            return {
                absoluteMin:
                    bounds.min,

                absoluteMax:
                    bounds.max,

                selectedMin:
                    selectedRange.min ??
                    bounds.min,

                selectedMax:
                    selectedRange.max ??
                    bounds.max,
            };
        }

        /**
         * Genera el icono de flecha utilizado por los grupos colapsables.
         */
        renderArrowIcon() {
            return `
        <svg
          class="icon icon-nav-arrow-down"
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M10 1.5L6 5.5L2 1.5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="square"
          />
        </svg>
      `;
        }

        /**
         * Genera la cabecera visual de un grupo de filtros.
         */
        renderFacetHeading(
            facet,
            activeCount
        ) {
            const label =
                this.escapeHtml(
                    this.getFacetLabel(facet)
                );

            return `
        <summary
          class="filter-group__heading"
        >
          <span>${label}</span>

          ${activeCount > 0
                    ? `
                <span
                  data-nosto-facet-count
                >
                  (${activeCount})
                </span>
              `
                    : ""
                }

          ${this.renderArrowIcon()}
        </summary>
      `;
        }

        /**
         * Genera el HTML de una opción de filtro con checkbox.
         */
        renderCheckbox({
            facet,
            value,
            label,
            count = 0,
            index,
            isColorGroup = false,
            disabled = false,
        }) {
            const field =
                this.normalizeString(
                    facet.field
                );

            const isSelected =
                this.facetManager.isSelected(
                    field,
                    value
                );

            const id = [
                "nosto-filter",
                this.createId(field),
                this.createId(value),
                index,
            ].join("-");

            const isHidden =
                index >=
                this.maxVisibleItems;

            const swatchClass = isColorGroup
                ? `swatch-${this
                    .normalizeString(value)
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/[^a-z0-9]+/g, "")}`
                : "";

            const itemClasses = [
                "sidebar__item",

                isColorGroup
                    ? "sidebar__item--color"
                    : "sidebar__item--pill",

                swatchClass,

                isSelected
                    ? "sidebar__item--active"
                    : "",

                disabled && !isSelected
                    ? "link--disable"
                    : isSelected
                        ? "link--remove"
                        : "link--add",

                isHidden
                    ? "hidden"
                    : "",
            ]
                .filter(Boolean)
                .join(" ");

            return `
        <li
            class="${itemClasses}"
            ${isColorGroup
                ? `data-nosto-color-option data-nosto-color-name="${this.escapeAttribute(label)}"`
                : ""
            }
            ${isHidden
                ? `
                    data-nosto-extra-item
                    data-link-hidden
                    `
                : ""
            }
        >
          <input
            type="checkbox"

            id="${this.escapeAttribute(
                    id
                )}"

            value="${this.escapeAttribute(
                    value
                )}"

            data-nosto-facet-input

            data-nosto-facet-field="${this.escapeAttribute(
                    field
                )}"

            ${isColorGroup
                    ? 'data-nosto-color-group="true"'
                    : ""
                }

            ${isSelected
                    ? "checked"
                    : ""
                }

            ${disabled && !isSelected
                    ? "disabled"
                    : ""
                }
          >

          <label
            for="${this.escapeAttribute(
                    id
                )}"
          >
            <span>
              ${this.escapeHtml(label)}
            </span>

            ${this.showCounts
                    ? `
                  <span
                    class="filter-value-count hidden"
                    aria-hidden="true"
                  >
                    (${Number(count) || 0})
                  </span>
                `
                    : ""
                }
          </label>
        </li>
      `;
        }

        /**
         * Genera la lista de opciones normales de un facet.
         */
        renderValueList(facet) {
            const values =
                this.sortFacetValues(
                    facet,
                    this.getFacetValues(
                        facet
                    )
                );

            const itemsHtml =
                values
                    .map(
                        (
                            facetValue,
                            index
                        ) => {
                            const value =
                                this.getFacetItemValue(
                                    facetValue
                                );

                            if (!value) {
                                return "";
                            }

                            const count =
                                this.getFacetValueCount(
                                    facetValue
                                );

                            return this.renderCheckbox({
                                facet,
                                value,

                                label:
                                    this.getValueLabel(
                                        facet.field,
                                        value
                                    ),

                                count,
                                index,

                                disabled:
                                    count <= 0,
                            });
                        }
                    )
                    .join("");

            return this.renderListWrapper(
                itemsHtml,
                values.length
            );
        }

        /**
         * Genera la lista de grupos de color configurados.
         */
        renderColorList(facet) {
            const groups =
                this.getColorGroups(facet);

            const itemsHtml =
                groups
                    .map(
                        (
                            group,
                            index
                        ) =>
                            this.renderCheckbox({
                                facet,

                                value:
                                    group.value,

                                label:
                                    group.label,

                                count:
                                    group.count,

                                index,

                                isColorGroup:
                                    true,

                                disabled:
                                    group.count <= 0,
                            })
                    )
                    .join("");

            return this.renderListWrapper(
                itemsHtml,
                groups.length
            );
        }

        /**
 * Envuelve las opciones dentro de la estructura de lista del theme.
 */
        renderListWrapper(
            itemsHtml,
            itemCount
        ) {
            return `
            <ul class="collection-nav">
            ${itemsHtml}
            </ul>

            ${itemCount >
                    this.maxVisibleItems
                    ? `
                  <div class="filter-group__show-more">
                    <button
                      type="button"
                      class="collection__sidebar__link"
                      data-nosto-show-more
                      aria-expanded="false"
                    >
                      <span
                        class="filter-group__show-more-icon"
                        aria-hidden="true"
                      >
                        ${this.renderShowMoreIcon()}
                      </span>

                      <span
                        data-nosto-show-more-label
                      >
                        Mostrar más
                      </span>
                    </button>
                  </div>
                `
                    : ""
                }
            `;
        }

        /**
         * Genera el filtro de precio utilizando el Web Component range-slider.
         */
        renderPriceFacet(facet) {
            const price =
                this.getPriceState(facet);

            return `
        <div
          class="filter__price"

          data-range-holder
          data-nosto-price-root

          data-nosto-price-field="${this.escapeAttribute(
                facet.field
            )}"

          data-price-min="${price.absoluteMin}"
          data-price-max="${price.absoluteMax}"
        >
          <range-slider
            class="filter__price__range range"

            data-range-slider
            data-range-filter-update

            data-se-min="${price.absoluteMin}"
            data-se-max="${price.absoluteMax}"

            data-se-min-value="${price.selectedMin}"
            data-se-max-value="${price.selectedMax}"

            data-se-step="1"
          >
            <div
              class="range__dot range__dot--left"
              data-range-left
            >
              <span>&nbsp;</span>
            </div>

            <div
              class="range__dot range__dot--right"
              data-range-right
            >
              <span>&nbsp;</span>
            </div>

            <div class="range__line">
              <span data-range-line>
                &nbsp;
              </span>
            </div>
          </range-slider>

          <div class="filter__price__fields">
            <div
              class="filter__price__field filter__price__from"
            >
              <span
                class="filter__price__currency"
              >
                ${this.escapeHtml(
                this.currencySymbol
            )}
              </span>

              <input
                data-field-price-min
                data-nosto-price-min-input

                class="filter__price__input"

                type="number"
                inputmode="numeric"

                value="${price.selectedMin ===
                    price.absoluteMin
                    ? ""
                    : price.selectedMin
                }"

                placeholder="${price.absoluteMin}"

                min="${price.absoluteMin}"
                max="${price.absoluteMax}"
              >

              <label>
                Desde
              </label>
            </div>

            <div
              class="filter__price__spacer"
            >
              -
            </div>

            <div
              class="filter__price__field filter__price__to"
            >
              <span
                class="filter__price__currency"
              >
                ${this.escapeHtml(
                    this.currencySymbol
                )}
              </span>

              <input
                data-field-price-max
                data-nosto-price-max-input

                class="filter__price__input"

                type="number"
                inputmode="numeric"

                value="${price.selectedMax ===
                    price.absoluteMax
                    ? ""
                    : price.selectedMax
                }"

                placeholder="${price.absoluteMax}"

                min="${price.absoluteMin}"
                max="${price.absoluteMax}"
              >

              <label>
                Hasta
              </label>
            </div>
          </div>

          ${this.showPriceLabels
                    ? `
                <div
                  class="filter__price__selected"
                  aria-live="polite"
                >
                  <span
                    data-nosto-price-min-label
                  >
                    ${this.formatMoney(
                        price.selectedMin
                    )}
                  </span>

                  <span aria-hidden="true">
                    -
                  </span>

                  <span
                    data-nosto-price-max-label
                  >
                    ${this.formatMoney(
                        price.selectedMax
                    )}
                  </span>
                </div>
              `
                    : ""
                }
        </div>
      `;
        }

        /**
         * Genera el HTML de un grupo completo de filtros.
         */
        renderFacetGroup(facet) {
            const field =
                this.normalizeString(
                    facet.field
                );

            const activeCount =
                this.facetManager
                    .getActiveCount(field);

            const contentHtml =
                this.isPriceFacet(facet)
                    ? this.renderPriceFacet(
                        facet
                    )
                    : this.isColorFacet(facet)
                        ? this.renderColorList(
                            facet
                        )
                        : this.renderValueList(
                            facet
                        );

            return `
        <div
          class="filter-group"

          data-nosto-facet-group

          data-nosto-facet-field="${this.escapeAttribute(
                field
            )}"
        >
          <details
            ${this.defaultOpen
                    ? "open"
                    : ""
                }
          >
            ${this.renderFacetHeading(
                    facet,
                    activeCount
                )}

            <div
              class="filter-group__body"
            >
              <div
                class="filter-group__content"
              >
                ${contentHtml}
              </div>
            </div>
          </details>
        </div>
      `;
        }

        /**
         * Renderiza la lista completa de facets recibidos desde Nosto.
         */
        render(
            facets = [],
            {
                markApplied = false,
            } = {}
        ) {
            this.validateDependencies();

            this.facets =
                Array.isArray(facets)
                    ? facets.filter(
                        (facet) =>
                            !this.shouldHideFacet(
                                facet
                            )
                    )
                    : [];

            const sortedFacets =
                this.sortFacets(
                    this.facets
                );

            this.container.innerHTML =
                sortedFacets
                    .map((facet) =>
                        this.renderFacetGroup(
                            facet
                        )
                    )
                    .join("");

            this.bindEvents();
            this.initializePriceControls();
            this.updateAllGroupStates();
            if (markApplied) {
                this.markCurrentStateAsApplied({
                    updateButtons: false,
                });
            }

            this.updateButtons();

            this.onRender?.({
                facets:
                    sortedFacets,

                container:
                    this.container,

                renderer:
                    this,

                facetManager:
                    this.facetManager,
            });

            this.log(
                "Facets renderizados.",
                sortedFacets
            );

            return sortedFacets;
        }

        /**
         * Registra los eventos delegados del renderer una sola vez.
         */
        bindEvents() {
            if (this.isBound) {
                return;
            }

            this.container.addEventListener(
                "change",
                this.boundHandleChange
            );

            this.container.addEventListener(
                "input",
                this.boundHandleInput
            );

            this.container.addEventListener(
                "click",
                this.boundHandleClick
            );

            this.container.addEventListener(
                "theme:range:update",
                this.boundHandleRangeUpdate
            );

            this.applyButton?.addEventListener(
                "click",
                this.boundHandleApply
            );

            this.clearButton?.addEventListener(
                "click",
                this.boundHandleClear
            );

            this.isBound = true;
        }

        /**
         * Elimina todos los eventos registrados por el renderer.
         */
        destroy() {
            if (!this.isBound) {
                return;
            }

            this.container.removeEventListener(
                "change",
                this.boundHandleChange
            );

            this.container.removeEventListener(
                "input",
                this.boundHandleInput
            );

            this.container.removeEventListener(
                "click",
                this.boundHandleClick
            );

            this.container.removeEventListener(
                "theme:range:update",
                this.boundHandleRangeUpdate
            );

            this.applyButton?.removeEventListener(
                "click",
                this.boundHandleApply
            );

            this.clearButton?.removeEventListener(
                "click",
                this.boundHandleClear
            );

            this.isBound = false;
        }

        /**
         * Reacciona a cambios confirmados en checkboxes e inputs de precio.
         */
        handleChange(event) {
            const facetInput =
                event.target.closest(
                    "[data-nosto-facet-input]"
                );

            if (facetInput) {
                this.handleFacetInputChange(
                    facetInput
                );

                return;
            }

            const priceInput =
                event.target.closest(
                    [
                        "[data-nosto-price-min-input]",
                        "[data-nosto-price-max-input]",
                    ].join(",")
                );

            if (!priceInput) {
                return;
            }

            const priceRoot =
                priceInput.closest(
                    "[data-nosto-price-root]"
                );

            if (!priceRoot) {
                return;
            }

            this.commitPriceRange(
                priceRoot,
                {
                    updateSlider: true,
                }
            );
        }

        /**
         * Aplica automáticamente los filtros cuando autoApply está activo.
         */
        applyAutomatically() {
            if (!this.autoApply) {
                return;
            }

            this.facetManager.apply({
                syncUrl:
                    this.facetManager
                        .syncUrl,
            });

            this.markCurrentStateAsApplied();
        }

        /**
         * Reacciona a cambios de un checkbox y actualiza el FacetManager.
         */
        handleFacetInputChange(input) {
            const field =
                input.dataset
                    .nostoFacetField;

            const value =
                input.value;

            this.facetManager.setValue(
                field,
                value,
                input.checked,
                {
                    emit:
                        this.autoApply,

                    syncUrl:
                        this.autoApply &&
                        this.facetManager.syncUrl,
                }
            );

            this.syncFacetItemState(
                input
            );

            this.updateGroupState(
                field
            );

            this.updateDirtyState();
            this.applyAutomatically();
        }

        /**
         * Reacciona mientras el usuario escribe en los campos de precio.
         */
        handleInput(event) {
            const priceInput =
                event.target.closest(
                    [
                        "[data-nosto-price-min-input]",
                        "[data-nosto-price-max-input]",
                    ].join(",")
                );

            if (!priceInput) {
                return;
            }

            const priceRoot =
                priceInput.closest(
                    "[data-nosto-price-root]"
                );

            if (!priceRoot) {
                return;
            }

            this.updatePriceLabels(
                priceRoot
            );

            this.updateDirtyState();
        }

        /**
 * Reacciona a controles internos de los filtros.
 */
        handleClick(event) {
            const showMoreButton =
                event.target.closest(
                    "[data-nosto-show-more]"
                );

            if (
                !showMoreButton ||
                !this.container.contains(
                    showMoreButton
                )
            ) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            this.toggleShowMore(
                showMoreButton
            );
        }

        /**
 * Muestra u oculta las opciones adicionales de un grupo.
 */
        toggleShowMore(button) {
            const group =
                button.closest(
                    "[data-nosto-facet-group]"
                );

            if (!group) {
                return;
            }

            const extraItems =
                group.querySelectorAll(
                    "[data-nosto-extra-item]"
                );

            const nextExpanded =
                button.getAttribute(
                    "aria-expanded"
                ) !== "true";

            extraItems.forEach(
                (item) => {
                    item.classList.toggle(
                        "hidden",
                        !nextExpanded
                    );

                    if (nextExpanded) {
                        item.removeAttribute(
                            "data-link-hidden"
                        );
                    } else {
                        item.setAttribute(
                            "data-link-hidden",
                            ""
                        );
                    }
                }
            );

            group.dataset.nostoExpanded =
                String(nextExpanded);

            button.setAttribute(
                "aria-expanded",
                String(nextExpanded)
            );

            button.classList.toggle(
                "is-expanded",
                nextExpanded
            );

            const label =
                button.querySelector(
                    "[data-nosto-show-more-label]"
                );

            if (label) {
                label.textContent =
                    nextExpanded
                        ? "Mostrar menos"
                        : "Mostrar más";
            }
        }

        /**
         * Guarda el rango seleccionado cuando el slider termina de moverse.
         */
        handleRangeUpdate(event) {
            const slider =
                event.target.closest(
                    "[data-range-slider]"
                );

            if (!slider) {
                return;
            }

            const priceRoot =
                slider.closest(
                    "[data-nosto-price-root]"
                );

            if (!priceRoot) {
                return;
            }

            this.commitPriceRange(
                priceRoot,
                {
                    updateSlider: false,
                }
            );

            this.updateDirtyState();
        }

        /**
         * Obtiene y normaliza los valores actuales de un filtro de precio.
         */
        readPriceRange(priceRoot) {
            const absoluteMin =
                Number(
                    priceRoot.dataset
                        .priceMin
                );

            const absoluteMax =
                Number(
                    priceRoot.dataset
                        .priceMax
                );

            const minInput =
                priceRoot.querySelector(
                    "[data-nosto-price-min-input]"
                );

            const maxInput =
                priceRoot.querySelector(
                    "[data-nosto-price-max-input]"
                );

            let min =
                this.normalizeNullableNumber(
                    minInput?.value
                );

            let max =
                this.normalizeNullableNumber(
                    maxInput?.value
                );

            min =
                min == null
                    ? absoluteMin
                    : Math.max(
                        absoluteMin,
                        Math.min(
                            min,
                            absoluteMax
                        )
                    );

            max =
                max == null
                    ? absoluteMax
                    : Math.max(
                        absoluteMin,
                        Math.min(
                            max,
                            absoluteMax
                        )
                    );

            if (min > max) {
                [min, max] = [
                    max,
                    min,
                ];
            }

            return {
                absoluteMin,
                absoluteMax,
                min,
                max,
                minInput,
                maxInput,
            };
        }

        /**
         * Guarda un rango de precio y sincroniza su interfaz visual.
         */
        commitPriceRange(
            priceRoot,
            {
                updateSlider = true,
            } = {}
        ) {
            const range =
                this.readPriceRange(
                    priceRoot
                );

            if (range.minInput) {
                range.minInput.value =
                    range.min ===
                        range.absoluteMin
                        ? ""
                        : String(range.min);
            }

            if (range.maxInput) {
                range.maxInput.value =
                    range.max ===
                        range.absoluteMax
                        ? ""
                        : String(range.max);
            }

            if (updateSlider) {
                this.syncSliderFromInputs(
                    priceRoot,
                    range
                );
            }

            this.facetManager.setPrice(
                range.min,
                range.max,
                {
                    emit:
                        this.autoApply,

                    syncUrl:
                        this.autoApply &&
                        this.facetManager.syncUrl,

                    treatBoundsAsEmpty:
                        true,
                }
            );

            this.updatePriceLabels(
                priceRoot
            );

            this.updateGroupState(
                this.priceField
            );

            this.updateDirtyState();
            this.applyAutomatically();
        }

        /**
         * Sincroniza la posición del slider con los valores escritos manualmente.
         */
        syncSliderFromInputs(
            priceRoot,
            range = null
        ) {
            const currentRange =
                range ||
                this.readPriceRange(
                    priceRoot
                );

            const slider =
                priceRoot.querySelector(
                    "[data-range-slider]"
                );

            if (!slider) {
                return;
            }

            slider.setAttribute(
                "data-se-min-value",
                String(currentRange.min)
            );

            slider.setAttribute(
                "data-se-max-value",
                String(currentRange.max)
            );

            if (
                typeof slider.setMinValue ===
                "function"
            ) {
                slider.setMinValue(
                    currentRange.min
                );
            }

            if (
                typeof slider.setMaxValue ===
                "function"
            ) {
                slider.setMaxValue(
                    currentRange.max
                );
            }

            if (
                typeof slider.refresh ===
                "function"
            ) {
                slider.refresh();
            }
        }

        /**
         * Actualiza las etiquetas monetarias visibles del rango de precio.
         */
        updatePriceLabels(priceRoot) {
            if (!this.showPriceLabels) {
                return;
            }

            const range =
                this.readPriceRange(
                    priceRoot
                );

            const minLabel =
                priceRoot.querySelector(
                    "[data-nosto-price-min-label]"
                );

            const maxLabel =
                priceRoot.querySelector(
                    "[data-nosto-price-max-label]"
                );

            if (minLabel) {
                minLabel.textContent =
                    this.formatMoney(
                        range.min
                    );
            }

            if (maxLabel) {
                maxLabel.textContent =
                    this.formatMoney(
                        range.max
                    );
            }
        }

        /**
         * Inicializa o recalcula los controles de precio después del renderizado.
         */
        initializePriceControls() {
            requestAnimationFrame(() => {
                this.container
                    .querySelectorAll(
                        "[data-nosto-price-root]"
                    )
                    .forEach(
                        (priceRoot) => {
                            const slider =
                                priceRoot.querySelector(
                                    "[data-range-slider]"
                                );

                            if (
                                slider &&
                                typeof slider.refresh ===
                                "function"
                            ) {
                                slider.refresh();
                            }

                            this.updatePriceLabels(
                                priceRoot
                            );
                        }
                    );
            });
        }

        /**
         * Actualiza las clases visuales del elemento asociado a un checkbox.
         */
        syncFacetItemState(input) {
            const item =
                input.closest(
                    ".sidebar__item"
                );

            if (!item) {
                return;
            }

            item.classList.toggle(
                "sidebar__item--active",
                input.checked
            );

            item.classList.toggle(
                "link--remove",
                input.checked
            );

            item.classList.toggle(
                "link--add",
                !input.checked &&
                !input.disabled
            );

            item.classList.toggle(
                "link--disable",
                input.disabled &&
                !input.checked
            );
        }

        /**
         * Busca el grupo visual correspondiente a un campo.
         */
        getFacetGroup(field) {
            const safeField =
                typeof CSS !== "undefined" &&
                    typeof CSS.escape === "function"
                    ? CSS.escape(field)
                    : String(field).replace(
                        /"/g,
                        '\\"'
                    );

            return this.container.querySelector(
                `[data-nosto-facet-group][data-nosto-facet-field="${safeField}"]`
            );
        }

        /**
         * Actualiza el conteo y estado visual de un grupo de filtros.
         */
        updateGroupState(field) {
            const group =
                this.getFacetGroup(field);

            if (!group) {
                return;
            }

            const activeCount =
                this.facetManager
                    .getActiveCount(field);

            const heading =
                group.querySelector(
                    ".filter-group__heading"
                );

            let countElement =
                group.querySelector(
                    "[data-nosto-facet-count]"
                );

            if (
                activeCount > 0 &&
                !countElement
            ) {
                countElement =
                    document.createElement(
                        "span"
                    );

                countElement.setAttribute(
                    "data-nosto-facet-count",
                    ""
                );

                const icon =
                    heading?.querySelector(
                        "svg"
                    );

                heading?.insertBefore(
                    countElement,
                    icon || null
                );
            }

            if (countElement) {
                if (activeCount > 0) {
                    countElement.textContent =
                        `(${activeCount})`;
                } else {
                    countElement.remove();
                }
            }

            group.classList.toggle(
                "filter-group--active",
                activeCount > 0
            );
        }

        /**
         * Actualiza todos los grupos según el estado actual del FacetManager.
         */
        updateAllGroupStates() {
            this.container
                .querySelectorAll(
                    "[data-nosto-facet-group]"
                )
                .forEach((group) => {
                    const field =
                        group.dataset
                            .nostoFacetField;

                    if (!field) return;

                    this.updateGroupState(
                        field
                    );
                });
        }

        /**
 * Actualiza la disponibilidad de los botones Aplicar y Limpiar.
 */
        updateButtons() {
            const hasActiveFilters =
                this.facetManager
                    ?.hasActiveFilters?.() ||
                false;

            const isDirty =
                this.autoApply
                    ? false
                    : this.calculateDirtyState();

            this.isDirty =
                isDirty;

            if (this.applyButton) {
                this.applyButton.disabled =
                    !this.autoApply &&
                    !isDirty;

                this.applyButton.classList.toggle(
                    "is-disabled",
                    !this.autoApply &&
                    !isDirty
                );

                this.applyButton.classList.toggle(
                    "is-dirty",
                    isDirty
                );

                this.applyButton.setAttribute(
                    "aria-disabled",
                    String(
                        !this.autoApply &&
                        !isDirty
                    )
                );
            }

            if (this.clearButton) {
                this.clearButton.disabled =
                    !hasActiveFilters;

                this.clearButton.classList.toggle(
                    "is-disabled",
                    !hasActiveFilters
                );

                this.clearButton.setAttribute(
                    "aria-disabled",
                    String(
                        !hasActiveFilters
                    )
                );
            }

            this.container?.classList.toggle(
                "is-dirty",
                isDirty
            );

            this.container?.setAttribute(
                "data-nosto-facets-dirty",
                String(isDirty)
            );
        }

        /**
 * Aplica los cambios pendientes de filtros.
 */
        handleApply(event) {
            event?.preventDefault();

            if (
                !this.autoApply &&
                !this.calculateDirtyState()
            ) {
                return;
            }

            this.facetManager.apply({
                syncUrl:
                    this.facetManager
                        .syncUrl,
            });

            this.markCurrentStateAsApplied();

            const drawerTrigger =
                document.querySelector(
                    '[aria-controls^="filter-groups-"]'
                );

            if (
                drawerTrigger?.getAttribute(
                    "aria-expanded"
                ) === "true"
            ) {
                drawerTrigger.click();
            }

            this.onApply?.({
                state:
                    this.facetManager
                        .getState(),

                activeState:
                    this.facetManager
                        .getActiveState(),

                selectedColorGroupsByField:
                    this.facetManager
                        .getColorGroupState?.() ||
                    {},

                renderer:
                    this,

                facetManager:
                    this.facetManager,
            });
        }

        /**
 * Limpia todos los filtros y aplica inmediatamente el estado vacío.
 */
        handleClear(event) {
            event?.preventDefault();

            const hasActiveFilters =
                this.facetManager
                    .hasActiveFilters();

            if (
                !hasActiveFilters &&
                !this.calculateDirtyState()
            ) {
                return;
            }

            this.facetManager.reset({
                emit: true,

                syncUrl:
                    this.facetManager
                        .syncUrl,
            });

            this.updateActiveFilterCountBadges(0);

            this.markCurrentStateAsApplied({
                updateButtons: false,
            });

            this.render(
                this.facets
            );

            this.updateButtons();

            this.onClear?.({
                renderer:
                    this,

                facetManager:
                    this.facetManager,
            });
        }

        /**
         * Sincroniza checkboxes y precio con el estado actual del FacetManager.
         */
        syncFromManager() {
            this.container
                .querySelectorAll(
                    "[data-nosto-facet-input]"
                )
                .forEach((input) => {
                    const field =
                        input.dataset
                            .nostoFacetField;

                    input.checked =
                        this.facetManager
                            .isSelected(
                                field,
                                input.value
                            );

                    this.syncFacetItemState(
                        input
                    );
                });

            const priceRoot =
                this.container.querySelector(
                    `[data-nosto-price-root][data-nosto-price-field="${this.priceField}"]`
                );

            if (priceRoot) {
                const bounds =
                    this.facetManager
                        .getPriceBounds();

                const range =
                    this.facetManager
                        .getRange(
                            this.priceField
                        ) || {
                        min: null,
                        max: null,
                    };

                const min =
                    range.min ??
                    bounds.min;

                const max =
                    range.max ??
                    bounds.max;

                const minInput =
                    priceRoot.querySelector(
                        "[data-nosto-price-min-input]"
                    );

                const maxInput =
                    priceRoot.querySelector(
                        "[data-nosto-price-max-input]"
                    );

                if (minInput) {
                    minInput.value =
                        min === bounds.min
                            ? ""
                            : String(min);
                }

                if (maxInput) {
                    maxInput.value =
                        max === bounds.max
                            ? ""
                            : String(max);
                }

                this.syncSliderFromInputs(
                    priceRoot
                );

                this.updatePriceLabels(
                    priceRoot
                );
            }

            this.updateAllGroupStates();
            this.updateButtons();
        }

        /**
         * Sustituye el contenedor utilizado por el renderer.
         */
        setContainer(container) {
            const nextContainer =
                this.resolveElement(
                    container
                );

            if (!nextContainer) {
                throw new Error(
                    "No fue posible establecer el contenedor de facets."
                );
            }

            this.destroy();

            this.container =
                nextContainer;

            this.bindEvents();

            return this.container;
        }

        /**
         * Elimina todo el contenido renderizado sin modificar el FacetManager.
         */
        clear() {
            this.container?.replaceChildren();

            this.facets = [];

            this.updateButtons();
        }
    }

    global.NostoFacetRenderer =
        NostoFacetRenderer;
})(window);
